'use client';
import React from 'react';
import * as Styled from './styled';
import Link from 'next/link';
import { Row } from '@/ui/primatives';

import { IRepository } from '@/types';
import { Config } from '@/hooks/useConfig';
import { IRepositoryBranch } from '@/lib/repositoryBranchesApi';
import useCommitsBetween from '@/hooks/useCommitsBetween';
import pluralize from 'pluralize';
import { CommitUser } from '@/components/Commits/CommitDetails';
import { toTimeAgo } from '@/lib/dateTime';
import { Simple as Tooltip } from '@/ui/Tooltip';
import Tag from '@/ui/Tag';
import Button from '@/ui/Button';
import * as Icon from '@/ui/Icon';
import useAuthorization from '@/hooks/useAuthorization';

const Branch: React.FC<{ repository: IRepository; branch: IRepositoryBranch; mergeRequestsEnabled: boolean }> = ({
  repository,
  branch,
  mergeRequestsEnabled,
}) => {
  const auth = useAuthorization();
  const canEdit = auth.forRepository(repository).canEdit();

  const { commits, isLoading } = useCommitsBetween({
    repository,
    base: repository.default_branch || 'main',
    head: branch.name,
  });
  const hasForwardCommits = (commits?.length || 0) > 0;

  return (
    <Styled.Branch>
      <td>
        <Styled.File>
          <Tag variant="info" small>
            <Icon.Branch size="12" />
            <Link
              href={`/${repository.namespace}/${repository.name}/dir/${branch.name}`}
              className="link filename text-line-truncate"
            >
              {branch.name}
            </Link>
          </Tag>

          {branch.is_head && (
            <Tag small variant="success">
              HEAD
            </Tag>
          )}
          {branch.is_collaboration_enabled && (
            <Tag variant="primary" pill small>
              Collaboration open
            </Tag>
          )}
        </Styled.File>
      </td>
      <td>
        {branch.commit && (
          <Row alignItems="center" gap={10}>
            <CommitUser commit={branch.commit} />
            <span>{toTimeAgo(branch.commit?.timestamp)}</span>
            <Tooltip content={branch.commit?.message}>
              <Icon.InfoCircle size="14" />
            </Tooltip>
          </Row>
        )}
      </td>
      <td>
        <Styled.CommitDiff>
          {hasForwardCommits && (
            <span style={{ whiteSpace: 'nowrap' }}>
              {pluralize('commit', commits?.length, true)} ahead of {/* {commits?.length} ahead of{' '} */}
              <Tag variant="info" small>
                <Icon.Branch size="12" />
                {repository.default_branch}
              </Tag>
            </span>
          )}
          {isLoading && 'Loading commits...'}
        </Styled.CommitDiff>
      </td>
      <td>
        {mergeRequestsEnabled && (
          <div style={{ textAlign: 'right' }}>
            <Button.Secondary
              small
              disabled={!hasForwardCommits || !canEdit}
              style={{ background: 'white', whiteSpace: 'nowrap' }}
              href={`/${repository.namespace}/${repository.name}/compare/branch/${repository.default_branch}..${branch.name}`}
            >
              <Icon.Merge size="12" />
              Create merge request
            </Button.Secondary>
          </div>
        )}
      </td>
    </Styled.Branch>
  );
};

const BranchList: React.FC<{ repository: IRepository; name: string; branches?: IRepositoryBranch[] }> = ({
  repository,
  name,
  branches,
}) => {
  const { config } = Config.useContainer();
  const mergeRequestsEnabled = !!config?.merge_requests_enabled;

  return (
    <Styled.BranchList>
      <Styled.BranchListHeader>
        <tr>
          <th>Branch</th>
          <th>Updated</th>
          <th>Commits ahead</th>
          <th></th>
        </tr>
      </Styled.BranchListHeader>
      <Styled.BranchListBody>
        {branches?.map((branch) => (
          <Branch
            key={[branch.name, branch.commit.id].join('-')}
            repository={repository}
            branch={branch}
            mergeRequestsEnabled={mergeRequestsEnabled}
          />
        ))}
      </Styled.BranchListBody>
    </Styled.BranchList>
  );
};

export default BranchList;
